/**
 * The sidebar as data, grouped by job (section 2): Home, Library (the business's own nouns), Live
 * & schedule (only for brands that do live), Your app, Analytics, Pricing & access, Settings. Which item is
 * current is a matter of the route, not of the URL's exact text: an item's editor keeps its list
 * selected.
 */

import type { NavigationItem } from "@web-ui/types/navigation";
import type { EntityType } from "@/api/admin";
import { href, type Route } from "@/lib/route";
import { libraryTypes, noun, type Pack } from "@/vocabulary/vocabulary";

export interface NavInput {
  brand: string;
  pack: Pack;
  counts: Partial<Record<EntityType, number>>;
  /** The brand has live switched on, or already has live events. */
  showLive: boolean;
  isOwner: boolean;
  /** The current section: the Library group is open everywhere but on Settings pages, Settings only there. */
  section?: Route["section"];
}

export function consoleNavigation({ brand, pack, counts, showLive, isOwner, section }: NavInput): NavigationItem[] {
  return [
    { title: "Home", icon: "dashboard", href: href({ brand, section: "home" }) },
    {
      title: "Library", icon: "layers", open: section !== "settings",
      list: libraryTypes(pack, counts).map((type) => ({ title: noun(pack, type, "many"), href: href({ brand, section: "library", type }) })),
    },
    ...(showLive ? [{ title: "Live & schedule", icon: "calendar-1", href: href({ brand, section: "live" }) }] : []),
    { title: "Your app", icon: "mobile", href: href({ brand, section: "app" }) },
    { title: "Analytics", icon: "chart", href: href({ brand, section: "analytics" }) },
    { title: "Pricing & access", icon: "wallet", href: href({ brand, section: "pricing" }) },
    {
      title: "Settings", icon: "edit-profile", open: section === "settings",
      list: [
        { title: "Brand", href: href({ brand, section: "settings", page: "brand" }) },
        { title: "Team", href: href({ brand, section: "settings", page: "team" }) },
        { title: "History", href: href({ brand, section: "settings", page: "history" }) },
        { title: "Files", href: href({ brand, section: "settings", page: "files" }) },
        ...(isOwner ? [{ title: "Advanced", href: href({ brand, section: "settings", page: "advanced" }) }] : []),
      ],
    },
  ];
}

/** The brand does live: it has live switched on, or already has live events (Live & schedule, Analytics › Live). */
export function showsLive(features: { live: boolean } | undefined, counts: Partial<Record<EntityType, number>>): boolean {
  return !!features?.live || (counts.liveEvent ?? 0) > 0;
}

/** The sidebar link that stands for this route (a detail page selects its list). */
export function activeHref(route: Route): string {
  switch (route.section) {
    case "library": return href({ brand: route.brand, section: "library", type: route.type ?? "title" });
    case "live":
    case "app":
    case "analytics":
    case "home":
    case "pricing": return href({ brand: route.brand, section: route.section });
    case "settings": return href({ brand: route.brand, section: "settings", page: route.page ?? "brand" });
    default: return "";
  }
}
